import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  LogOut, Users, BarChart3, DollarSign, Zap, Plus, Search, Eye,
  ToggleLeft, ToggleRight, Copy, ShieldCheck, Store, Send, TrendingUp,
  CreditCard, Trash2, ChevronLeft, ChevronRight, ExternalLink, UtensilsCrossed, Pencil
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: 'overview', label: 'Vue d\'ensemble', icon: BarChart3 },
  { id: 'vendors', label: 'Vendeurs', icon: Users },
  { id: 'merchants', label: 'Marchands', icon: Store },
  { id: 'spins', label: 'Spins', icon: Zap },
  { id: 'campaigns', label: 'Campagnes', icon: Send },
  { id: 'menus', label: 'Menus', icon: UtensilsCrossed },
];

export default function AdminDashboard() {
  const { user, logout, axiosAuth } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [spins, setSpins] = useState({ spins: [], total: 0, page: 1, pages: 0 });
  const [campaigns, setCampaigns] = useState({ campaigns: [], total: 0, page: 1, pages: 0 });
  const [search, setSearch] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [dialogType, setDialogType] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [creditDialog, setCreditDialog] = useState(null);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [form, setForm] = useState({});
  const [creating, setCreating] = useState(false);
  const [spinPage, setSpinPage] = useState(1);
  const [campPage, setCampPage] = useState(1);
  const [menus, setMenus] = useState([]);
  const [editMenu, setEditMenu] = useState(null);
  const [menuForm, setMenuForm] = useState(null);

  const api = useCallback(() => axiosAuth(), [axiosAuth]);

  const fetchStats = useCallback(async () => {
    try { const { data } = await api().get('/admin/stats'); setStats(data); } catch (e) { console.error(e); }
  }, [api]);

  const fetchVendors = useCallback(async () => {
    try { const { data } = await api().get(`/admin/vendors${search ? `?search=${encodeURIComponent(search)}` : ''}`); setVendors(data.vendors); } catch (e) { console.error(e); }
  }, [api, search]);

  const fetchMerchants = useCallback(async () => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (vendorFilter) params.set('vendor_id', vendorFilter);
    const qs = params.toString();
    try { const { data } = await api().get(`/admin/merchants${qs ? `?${qs}` : ''}`); setMerchants(data.merchants); } catch (e) { console.error(e); }
  }, [api, search, vendorFilter]);

  const fetchSpins = useCallback(async (page = 1) => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    params.set('page', page);
    params.set('limit', 30);
    try { const { data } = await api().get(`/admin/spins?${params}`); setSpins(data); } catch (e) { console.error(e); }
  }, [api, search]);

  const fetchCampaigns = useCallback(async (page = 1) => {
    const params = new URLSearchParams();
    params.set('page', page);
    params.set('limit', 30);
    try { const { data } = await api().get(`/admin/campaigns?${params}`); setCampaigns(data); } catch (e) { console.error(e); }
  }, [api]);

  const fetchMenus = useCallback(async () => {
    try { const { data } = await api().get('/admin/menus'); setMenus(data.menus); } catch (e) { console.error(e); }
  }, [api]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => {
    if (tab === 'vendors') fetchVendors();
    if (tab === 'merchants') { fetchMerchants(); fetchVendors(); }
    if (tab === 'spins') fetchSpins(spinPage);
    if (tab === 'campaigns') fetchCampaigns(campPage);
    if (tab === 'menus') fetchMenus();
  }, [tab, fetchVendors, fetchMerchants, fetchSpins, fetchCampaigns, fetchMenus, spinPage, campPage]);

  const handleLogout = () => { navigate('/'); setTimeout(() => logout(), 100); };
  const copy = (text) => { navigator.clipboard.writeText(text); toast.success('Copie !'); };
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const createVendor = async () => {
    if (!form.email || !form.password || !form.name) { toast.error('Champs obligatoires manquants'); return; }
    setCreating(true);
    try {
      await api().post('/admin/vendors', form);
      toast.success('Vendeur cree !');
      setDialogType(null); setForm({}); fetchVendors(); fetchStats();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setCreating(false); }
  };

  const createMerchant = async () => {
    if (!form.email || !form.password || !form.name || !form.business_name) { toast.error('Champs obligatoires manquants'); return; }
    setCreating(true);
    try {
      await api().post('/admin/merchants', { ...form, price: parseFloat(form.price) || 99 });
      toast.success('Marchand cree !');
      setDialogType(null); setForm({}); fetchMerchants(); fetchStats();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setCreating(false); }
  };

  const toggleVendorStatus = async (id) => {
    try { const { data } = await api().put(`/admin/vendors/${id}/status`); toast.success(`Statut : ${data.status}`); fetchVendors(); } catch (e) { toast.error('Erreur'); }
  };

  const toggleMerchantStatus = async (id) => {
    try { const { data } = await api().put(`/admin/merchants/${id}/status`); toast.success(`Statut : ${data.status}`); fetchMerchants(); } catch (e) { toast.error('Erreur'); }
  };

  const viewMerchantDetail = async (id) => {
    try { const { data } = await api().get(`/admin/merchants/${id}`); setDetailData(data); } catch (e) { toast.error('Erreur'); }
  };

  const adjustCredits = async () => {
    const amount = parseFloat(creditAmount);
    if (isNaN(amount) || amount === 0) { toast.error('Montant invalide'); return; }
    try {
      const { data } = await api().put(`/admin/credits/${creditDialog}`, { amount, reason: creditReason });
      toast.success(`Nouveau solde : ${data.balance}€`);
      setCreditDialog(null); setCreditAmount(''); setCreditReason('');
      fetchMerchants();
    } catch (e) { toast.error('Erreur'); }
  };

  const deleteUser = async (id, type) => {
    if (!window.confirm(`Supprimer ce ${type} ? Cette action est irreversible.`)) return;
    try {
      await api().delete(`/admin/users/${id}`);
      toast.success('Supprime !');
      if (type === 'vendeur') fetchVendors(); else fetchMerchants();
      fetchStats();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const openMenuEdit = (menu) => {
    setMenuForm(JSON.parse(JSON.stringify(menu)));
    setEditMenu(menu.id);
  };
  const saveMenu = async () => {
    if (!menuForm) return;
    try {
      await api().put(`/admin/menus/${editMenu}`, menuForm);
      toast.success('Menu sauvegarde !');
      setEditMenu(null); setMenuForm(null); fetchMenus();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const deleteMenu = async (id) => {
    if (!window.confirm('Supprimer ce menu ?')) return;
    try { await api().delete(`/admin/menus/${id}`); toast.success('Menu supprime'); fetchMenus(); } catch (e) { toast.error('Erreur'); }
  };
  const createNewMenu = async () => {
    const slug = window.prompt('Slug du menu (ex: snack-sevan) :');
    if (!slug) return;
    try {
      const { data } = await api().post('/admin/menus', { slug: slug.toLowerCase().replace(/\s+/g, '-'), business_name: slug, subtitle: '', logo_url: '', categories: [], owner_password: '' });
      toast.success('Menu cree !');
      fetchMenus();
      openMenuEdit(data.menu);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  return (
    <div className="min-h-screen" style={{ background: '#0F0F1A' }}>
      {/* Top Bar */}
      <div className="border-b border-white/10 bg-[#0F0F1A]/90 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#7C3AED] to-[#F59E0B] flex items-center justify-center font-bold text-xs">S</div>
            <span className="font-bold text-white hidden sm:block" style={{ fontFamily: 'Outfit' }}>Spin Review</span>
            <Badge className="bg-[#EF4444]/20 text-[#EF4444] border-[#EF4444]/30 text-xs" data-testid="admin-badge">Admin</Badge>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-[#94A3B8] hidden sm:block">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={() => navigate('/vendor/safe-review')} className="text-emerald-400 hover:bg-emerald-400/10 gap-1.5" data-testid="admin-safe-review-btn">
              <ShieldCheck className="w-4 h-4" /> <span className="hidden sm:inline">Safe Review</span>
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-[#94A3B8] hover:text-white" data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-white/5 bg-[#0F0F1A]/60 sticky top-14 z-40">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <div className="flex gap-1 overflow-x-auto py-2 scrollbar-hide">
            {TABS.map(t => (
              <button key={t.id} onClick={() => { setTab(t.id); setSearch(''); setVendorFilter(''); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${tab === t.id ? 'bg-[#7C3AED] text-white' : 'text-[#94A3B8] hover:text-white hover:bg-white/5'}`}
                data-testid={`admin-tab-${t.id}`}>
                <t.icon className="w-4 h-4" />{t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        {/* OVERVIEW TAB */}
        {tab === 'overview' && <OverviewTab stats={stats} />}

        {/* VENDORS TAB */}
        {tab === 'vendors' && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <h2 className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit' }}>Gestion des vendeurs</h2>
              <div className="flex gap-2 w-full sm:w-auto">
                <div className="relative flex-1 sm:flex-none">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                  <Input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && fetchVendors()}
                    placeholder="Rechercher..." className="pl-9 bg-white/5 border-white/10 text-white w-full sm:w-48 h-9" data-testid="admin-vendor-search" />
                </div>
                <Button onClick={() => { setDialogType('vendor'); setForm({ email: '', password: '', name: '', business_name: '' }); }}
                  className="btn-primary gap-1.5" data-testid="admin-create-vendor-btn"><Plus className="w-4 h-4" />Nouveau vendeur</Button>
              </div>
            </div>
            <div className="rounded-xl overflow-hidden" style={{ background: '#1A1A2E', border: '1px solid rgba(255,255,255,0.05)' }}>
              <Table>
                <TableHeader>
                  <TableRow className="border-white/5 hover:bg-transparent">
                    <TableHead className="text-[#94A3B8]">Vendeur</TableHead>
                    <TableHead className="text-[#94A3B8]">Email</TableHead>
                    <TableHead className="text-[#94A3B8]">Clients</TableHead>
                    <TableHead className="text-[#94A3B8]">Statut</TableHead>
                    <TableHead className="text-[#94A3B8]">Inscription</TableHead>
                    <TableHead className="text-[#94A3B8] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vendors.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center text-[#94A3B8] py-8">Aucun vendeur</TableCell></TableRow>
                  ) : vendors.map(v => (
                    <TableRow key={v.id} className="border-white/5 hover:bg-white/5">
                      <TableCell>
                        <p className="text-white font-medium text-sm">{v.business_name || v.name}</p>
                        <p className="text-[10px] text-[#94A3B8]">{v.name}</p>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-[#E2E8F0] font-mono">{v.email}</span>
                          <button onClick={() => copy(v.email)} className="text-[#94A3B8] hover:text-white"><Copy className="w-3 h-3" /></button>
                        </div>
                      </TableCell>
                      <TableCell><span className="text-sm font-semibold text-[#7C3AED]">{v.merchant_count || 0}</span></TableCell>
                      <TableCell>
                        <Badge className={v.status === 'active' ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-[#EF4444]/20 text-[#EF4444]'}>
                          {v.status === 'active' ? 'Actif' : 'Inactif'}
                        </Badge>
                      </TableCell>
                      <TableCell><span className="text-xs text-[#94A3B8]">{v.created_at?.slice(0, 10)}</span></TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => toggleVendorStatus(v.id)}
                            className={`h-7 w-7 p-0 ${v.status === 'active' ? 'text-[#EF4444] hover:bg-[#EF4444]/10' : 'text-[#10B981] hover:bg-[#10B981]/10'}`}
                            data-testid={`admin-toggle-vendor-${v.id}`}>
                            {v.status === 'active' ? <ToggleRight className="w-3.5 h-3.5" /> : <ToggleLeft className="w-3.5 h-3.5" />}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteUser(v.id, 'vendeur')}
                            className="text-[#EF4444] hover:bg-[#EF4444]/10 h-7 w-7 p-0"><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {/* MERCHANTS TAB */}
        {tab === 'merchants' && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <h2 className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit' }}>Gestion des marchands</h2>
              <div className="flex gap-2 w-full sm:w-auto flex-wrap">
                <div className="relative flex-1 sm:flex-none">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                  <Input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && fetchMerchants()}
                    placeholder="Rechercher..." className="pl-9 bg-white/5 border-white/10 text-white w-full sm:w-44 h-9" data-testid="admin-merchant-search" />
                </div>
                <Select value={vendorFilter} onValueChange={setVendorFilter}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white text-sm h-9 w-full sm:w-40">
                    <SelectValue placeholder="Tous vendeurs" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A2E] border-white/10">
                    <SelectItem value="all" className="text-white">Tous vendeurs</SelectItem>
                    {vendors.map(v => <SelectItem key={v.id} value={v.id} className="text-white">{v.business_name || v.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button onClick={() => { setDialogType('merchant'); setForm({ email: '', password: '', name: '', business_name: '', price: '99', address: '', business_phone: '', vendor_id: '' }); }}
                  className="btn-primary gap-1.5" data-testid="admin-create-merchant-btn"><Plus className="w-4 h-4" />Nouveau marchand</Button>
              </div>
            </div>
            <div className="rounded-xl overflow-hidden" style={{ background: '#1A1A2E', border: '1px solid rgba(255,255,255,0.05)' }}>
              <Table>
                <TableHeader>
                  <TableRow className="border-white/5 hover:bg-transparent">
                    <TableHead className="text-[#94A3B8]">Commerce</TableHead>
                    <TableHead className="text-[#94A3B8]">Vendeur</TableHead>
                    <TableHead className="text-[#94A3B8]">Spins</TableHead>
                    <TableHead className="text-[#94A3B8]">Credits</TableHead>
                    <TableHead className="text-[#94A3B8]">Prix</TableHead>
                    <TableHead className="text-[#94A3B8]">Statut</TableHead>
                    <TableHead className="text-[#94A3B8] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {merchants.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center text-[#94A3B8] py-8">Aucun marchand</TableCell></TableRow>
                  ) : merchants.map(m => (
                    <TableRow key={m.id} className="border-white/5 hover:bg-white/5">
                      <TableCell>
                        <p className="text-white font-medium text-sm">{m.business_name}</p>
                        <p className="text-[10px] text-[#94A3B8]">{m.email}</p>
                      </TableCell>
                      <TableCell><span className="text-xs text-[#94A3B8]">{m.vendor_name}</span></TableCell>
                      <TableCell><span className="text-sm font-semibold text-[#F59E0B]">{m.spin_count || 0}</span></TableCell>
                      <TableCell>
                        <button onClick={() => { setCreditDialog(m.id); setCreditAmount(''); setCreditReason(''); }}
                          className="text-sm font-semibold text-[#10B981] hover:underline">{m.credits || 0}€</button>
                      </TableCell>
                      <TableCell><span className="text-sm text-white">{(m.price || 99).toFixed(0)}€</span></TableCell>
                      <TableCell>
                        <Badge className={m.status === 'active' ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-[#EF4444]/20 text-[#EF4444]'}>
                          {m.status === 'active' ? 'Actif' : 'Inactif'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => viewMerchantDetail(m.id)}
                            className="text-[#7C3AED] hover:bg-[#7C3AED]/10 h-7 w-7 p-0" data-testid={`admin-view-merchant-${m.id}`}>
                            <Eye className="w-3.5 h-3.5" />
                          </Button>
                          <a href={`/spin/${m.id}`} target="_blank" rel="noopener noreferrer">
                            <Button variant="ghost" size="sm" className="text-[#F59E0B] hover:bg-[#F59E0B]/10 h-7 w-7 p-0"><ExternalLink className="w-3.5 h-3.5" /></Button>
                          </a>
                          <Button variant="ghost" size="sm" onClick={() => toggleMerchantStatus(m.id)}
                            className={`h-7 w-7 p-0 ${m.status === 'active' ? 'text-[#EF4444] hover:bg-[#EF4444]/10' : 'text-[#10B981] hover:bg-[#10B981]/10'}`}>
                            {m.status === 'active' ? <ToggleRight className="w-3.5 h-3.5" /> : <ToggleLeft className="w-3.5 h-3.5" />}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteUser(m.id, 'marchand')}
                            className="text-[#EF4444] hover:bg-[#EF4444]/10 h-7 w-7 p-0"><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {/* SPINS TAB */}
        {tab === 'spins' && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <h2 className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit' }}>Tous les spins</h2>
              <div className="relative flex-1 sm:flex-none">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                <Input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && fetchSpins(1)}
                  placeholder="Rechercher client..." className="pl-9 bg-white/5 border-white/10 text-white w-full sm:w-56 h-9" data-testid="admin-spin-search" />
              </div>
            </div>
            <div className="rounded-xl overflow-hidden" style={{ background: '#1A1A2E', border: '1px solid rgba(255,255,255,0.05)' }}>
              <Table>
                <TableHeader>
                  <TableRow className="border-white/5 hover:bg-transparent">
                    <TableHead className="text-[#94A3B8]">Date</TableHead>
                    <TableHead className="text-[#94A3B8]">Client</TableHead>
                    <TableHead className="text-[#94A3B8]">Commerce</TableHead>
                    <TableHead className="text-[#94A3B8]">Action</TableHead>
                    <TableHead className="text-[#94A3B8]">Resultat</TableHead>
                    <TableHead className="text-[#94A3B8]">Code</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {spins.spins?.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center text-[#94A3B8] py-8">Aucun spin</TableCell></TableRow>
                  ) : spins.spins?.map((s, i) => (
                    <TableRow key={s.id || i} className="border-white/5 hover:bg-white/5">
                      <TableCell><span className="text-xs text-[#94A3B8]">{s.created_at?.slice(0, 16).replace('T', ' ')}</span></TableCell>
                      <TableCell>
                        <p className="text-sm text-white">{s.first_name} {s.last_name}</p>
                        <p className="text-[10px] text-[#94A3B8]">{s.phone || s.email}</p>
                      </TableCell>
                      <TableCell><span className="text-xs text-[#E2E8F0]">{s.merchant_name}</span></TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-[10px] border-white/10 text-[#94A3B8]">{s.action_performed}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={s.is_losing ? 'bg-[#EF4444]/20 text-[#EF4444]' : 'bg-[#10B981]/20 text-[#10B981]'}>
                          {s.prize_won}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {s.prize_code ? (
                          <span className={`text-xs font-mono ${s.code_validated ? 'text-[#94A3B8] line-through' : 'text-[#F59E0B]'}`}>{s.prize_code}</span>
                        ) : <span className="text-xs text-[#94A3B8]">—</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {spins.pages > 1 && (
                <div className="flex items-center justify-center gap-3 py-3 border-t border-white/5">
                  <Button variant="ghost" size="sm" disabled={spinPage <= 1} onClick={() => setSpinPage(p => p - 1)} className="text-[#94A3B8] h-7">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-xs text-[#94A3B8]">{spins.page} / {spins.pages}</span>
                  <Button variant="ghost" size="sm" disabled={spinPage >= spins.pages} onClick={() => setSpinPage(p => p + 1)} className="text-[#94A3B8] h-7">
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* CAMPAIGNS TAB */}
        {tab === 'campaigns' && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit' }}>Toutes les campagnes</h2>
            <div className="rounded-xl overflow-hidden" style={{ background: '#1A1A2E', border: '1px solid rgba(255,255,255,0.05)' }}>
              <Table>
                <TableHeader>
                  <TableRow className="border-white/5 hover:bg-transparent">
                    <TableHead className="text-[#94A3B8]">Date</TableHead>
                    <TableHead className="text-[#94A3B8]">Commerce</TableHead>
                    <TableHead className="text-[#94A3B8]">Type</TableHead>
                    <TableHead className="text-[#94A3B8]">Envoyes</TableHead>
                    <TableHead className="text-[#94A3B8]">Cout</TableHead>
                    <TableHead className="text-[#94A3B8]">Sujet</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campaigns.campaigns?.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center text-[#94A3B8] py-8">Aucune campagne</TableCell></TableRow>
                  ) : campaigns.campaigns?.map((c, i) => (
                    <TableRow key={c.id || i} className="border-white/5 hover:bg-white/5">
                      <TableCell><span className="text-xs text-[#94A3B8]">{c.created_at?.slice(0, 16).replace('T', ' ')}</span></TableCell>
                      <TableCell><span className="text-xs text-[#E2E8F0]">{c.merchant_name}</span></TableCell>
                      <TableCell>
                        <Badge className={c.type === 'email' ? 'bg-[#7C3AED]/20 text-[#7C3AED]' : 'bg-[#3B82F6]/20 text-[#3B82F6]'}>
                          {c.type === 'email' ? 'Email' : 'SMS'}
                        </Badge>
                      </TableCell>
                      <TableCell><span className="text-sm text-white">{c.sent_count}/{c.recipients_count}</span></TableCell>
                      <TableCell><span className="text-sm text-[#10B981] font-semibold">{c.cost?.toFixed(2)}€</span></TableCell>
                      <TableCell><span className="text-xs text-[#94A3B8] truncate max-w-[200px] block">{c.subject || c.content_preview?.slice(0, 40)}</span></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {campaigns.pages > 1 && (
                <div className="flex items-center justify-center gap-3 py-3 border-t border-white/5">
                  <Button variant="ghost" size="sm" disabled={campPage <= 1} onClick={() => setCampPage(p => p - 1)} className="text-[#94A3B8] h-7">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-xs text-[#94A3B8]">{campaigns.page} / {campaigns.pages}</span>
                  <Button variant="ghost" size="sm" disabled={campPage >= campaigns.pages} onClick={() => setCampPage(p => p + 1)} className="text-[#94A3B8] h-7">
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* MENUS TAB */}
        {tab === 'menus' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit' }}>Menus digitaux</h2>
              <Button onClick={createNewMenu} className="btn-primary gap-1.5" data-testid="admin-create-menu-btn"><Plus className="w-4 h-4" />Nouveau menu</Button>
            </div>
            <div className="grid gap-4">
              {menus.length === 0 ? (
                <div className="rounded-xl p-8 text-center" style={{ background: '#1A1A2E', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <p className="text-[#94A3B8]">Aucun menu</p>
                </div>
              ) : menus.map(m => (
                <div key={m.id} className="rounded-xl p-5 flex items-center gap-4" style={{ background: '#1A1A2E', border: '1px solid rgba(255,255,255,0.05)' }}>
                  {m.logo_url && <img src={m.logo_url} alt="" className="w-12 h-12 rounded-full object-cover border border-white/10" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold">{m.business_name}</p>
                    <p className="text-xs text-[#94A3B8]">{m.subtitle} — {m.categories?.length || 0} categories</p>
                    <p className="text-xs text-[#7C3AED] font-mono mt-0.5">/menu/{m.slug}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <a href={`/menu/${m.slug}`} target="_blank" rel="noopener noreferrer">
                      <Button variant="ghost" size="sm" className="text-[#3B82F6] hover:bg-[#3B82F6]/10 h-8 w-8 p-0"><ExternalLink className="w-4 h-4" /></Button>
                    </a>
                    <Button variant="ghost" size="sm" onClick={() => openMenuEdit(m)} className="text-[#F59E0B] hover:bg-[#F59E0B]/10 h-8 w-8 p-0" data-testid={`edit-menu-${m.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => deleteMenu(m.id)} className="text-[#EF4444] hover:bg-[#EF4444]/10 h-8 w-8 p-0"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* MENU EDIT DIALOG */}
      <Dialog open={!!editMenu} onOpenChange={(v) => { if (!v) { setEditMenu(null); setMenuForm(null); } }}>
        <DialogContent className="bg-[#1A1A2E] border-white/10 text-white max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white" style={{ fontFamily: 'Outfit' }}>Modifier le menu — {menuForm?.business_name}</DialogTitle></DialogHeader>
          {menuForm && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-[#E2E8F0] text-xs">Nom du commerce</Label><Input value={menuForm.business_name || ''} onChange={e => setMenuForm(f => ({...f, business_name: e.target.value}))} className="mt-1 bg-white/5 border-white/10 text-white text-sm" /></div>
                <div><Label className="text-[#E2E8F0] text-xs">Sous-titre</Label><Input value={menuForm.subtitle || ''} onChange={e => setMenuForm(f => ({...f, subtitle: e.target.value}))} className="mt-1 bg-white/5 border-white/10 text-white text-sm" /></div>
              </div>
              <div><Label className="text-[#E2E8F0] text-xs">URL du logo</Label><Input value={menuForm.logo_url || ''} onChange={e => setMenuForm(f => ({...f, logo_url: e.target.value}))} className="mt-1 bg-white/5 border-white/10 text-white text-sm" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-[#E2E8F0] text-xs">Slug (URL)</Label><Input value={menuForm.slug || ''} onChange={e => setMenuForm(f => ({...f, slug: e.target.value}))} className="mt-1 bg-white/5 border-white/10 text-white text-sm" /></div>
                <div><Label className="text-[#E2E8F0] text-xs">Mot de passe proprietaire</Label><Input value={menuForm.owner_password || ''} onChange={e => setMenuForm(f => ({...f, owner_password: e.target.value}))} placeholder="Pour gestion par le client" className="mt-1 bg-white/5 border-white/10 text-white text-sm" /></div>
              </div>

              {/* Categories editor */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-white">Categories ({menuForm.categories?.length || 0})</p>
                  <Button size="sm" onClick={() => setMenuForm(f => ({...f, categories: [...(f.categories||[]), {name: 'Nouvelle categorie', items: []}]}))} className="btn-primary text-xs h-7"><Plus className="w-3 h-3 mr-1" />Categorie</Button>
                </div>
                {menuForm.categories?.map((cat, ci) => (
                  <div key={ci} className="rounded-lg border border-white/10 p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Input value={cat.name} onChange={e => { const cats = [...menuForm.categories]; cats[ci] = {...cats[ci], name: e.target.value}; setMenuForm(f => ({...f, categories: cats})); }}
                        className="flex-1 bg-white/5 border-white/10 text-white text-sm h-8 font-semibold" />
                      <Button size="sm" onClick={() => { const cats = [...menuForm.categories]; cats[ci] = {...cats[ci], items: [...cats[ci].items, {name: 'Nouveau plat', price: 0}]}; setMenuForm(f => ({...f, categories: cats})); }}
                        className="text-xs h-7 bg-[#10B981]/20 text-[#10B981] hover:bg-[#10B981]/30"><Plus className="w-3 h-3 mr-1" />Plat</Button>
                      <Button size="sm" variant="ghost" onClick={() => { const cats = menuForm.categories.filter((_, i) => i !== ci); setMenuForm(f => ({...f, categories: cats})); }}
                        className="text-[#EF4444] h-7 w-7 p-0"><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                    <div className="space-y-1">
                      {cat.items?.map((item, ii) => (
                        <div key={ii} className="flex items-center gap-2">
                          <Input value={item.name} onChange={e => { const cats = [...menuForm.categories]; cats[ci].items[ii] = {...cats[ci].items[ii], name: e.target.value}; setMenuForm(f => ({...f, categories: cats})); }}
                            className="flex-1 bg-white/5 border-white/10 text-white text-xs h-7" placeholder="Nom" />
                          <Input type="number" step="0.01" value={item.price} onChange={e => { const cats = [...menuForm.categories]; cats[ci].items[ii] = {...cats[ci].items[ii], price: parseFloat(e.target.value) || 0}; setMenuForm(f => ({...f, categories: cats})); }}
                            className="w-20 bg-white/5 border-white/10 text-white text-xs h-7 text-center" />
                          <span className="text-[10px] text-[#94A3B8]">€</span>
                          <Button size="sm" variant="ghost" onClick={() => { const cats = [...menuForm.categories]; cats[ci].items = cats[ci].items.filter((_, i) => i !== ii); setMenuForm(f => ({...f, categories: cats})); }}
                            className="text-[#EF4444] h-6 w-6 p-0"><Trash2 className="w-3 h-3" /></Button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <Button onClick={saveMenu} className="btn-accent w-full font-bold">Sauvegarder le menu</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={dialogType === 'vendor'} onOpenChange={(v) => { if (!v) setDialogType(null); }}>
        <DialogContent className="bg-[#1A1A2E] border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle className="text-white" style={{ fontFamily: 'Outfit' }}>Nouveau vendeur</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-[#E2E8F0] text-xs">Nom *</Label><Input value={form.name || ''} onChange={e => set('name', e.target.value)} placeholder="Jean Dupont" className="mt-1 bg-white/5 border-white/10 text-white text-sm" data-testid="admin-vendor-name" /></div>
            <div><Label className="text-[#E2E8F0] text-xs">Entreprise</Label><Input value={form.business_name || ''} onChange={e => set('business_name', e.target.value)} placeholder="Agence Marketing" className="mt-1 bg-white/5 border-white/10 text-white text-sm" /></div>
            <div><Label className="text-[#E2E8F0] text-xs">Email *</Label><Input type="email" value={form.email || ''} onChange={e => set('email', e.target.value)} placeholder="vendeur@email.com" className="mt-1 bg-white/5 border-white/10 text-white text-sm" data-testid="admin-vendor-email" /></div>
            <div><Label className="text-[#E2E8F0] text-xs">Mot de passe *</Label><Input value={form.password || ''} onChange={e => set('password', e.target.value)} placeholder="Min. 6 caracteres" className="mt-1 bg-white/5 border-white/10 text-white text-sm" data-testid="admin-vendor-password" /></div>
            <Button onClick={createVendor} disabled={creating} className="btn-primary w-full" data-testid="admin-vendor-submit">{creating ? 'Creation...' : 'Creer le vendeur'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* CREATE MERCHANT DIALOG */}
      <Dialog open={dialogType === 'merchant'} onOpenChange={(v) => { if (!v) setDialogType(null); }}>
        <DialogContent className="bg-[#1A1A2E] border-white/10 text-white max-w-lg">
          <DialogHeader><DialogTitle className="text-white" style={{ fontFamily: 'Outfit' }}>Nouveau marchand</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[#E2E8F0] text-xs">Nom du commerce *</Label><Input value={form.business_name || ''} onChange={e => set('business_name', e.target.value)} placeholder="Boulangerie" className="mt-1 bg-white/5 border-white/10 text-white text-sm" data-testid="admin-merchant-business" /></div>
              <div><Label className="text-[#E2E8F0] text-xs">Nom du gerant *</Label><Input value={form.name || ''} onChange={e => set('name', e.target.value)} placeholder="Jean Dupont" className="mt-1 bg-white/5 border-white/10 text-white text-sm" data-testid="admin-merchant-name" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[#E2E8F0] text-xs">Email *</Label><Input type="email" value={form.email || ''} onChange={e => set('email', e.target.value)} placeholder="client@email.com" className="mt-1 bg-white/5 border-white/10 text-white text-sm" data-testid="admin-merchant-email" /></div>
              <div><Label className="text-[#E2E8F0] text-xs">Mot de passe *</Label><Input value={form.password || ''} onChange={e => set('password', e.target.value)} placeholder="Min. 6 caracteres" className="mt-1 bg-white/5 border-white/10 text-white text-sm" data-testid="admin-merchant-password" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[#E2E8F0] text-xs">Adresse</Label><Input value={form.address || ''} onChange={e => set('address', e.target.value)} placeholder="12 rue..." className="mt-1 bg-white/5 border-white/10 text-white text-sm" /></div>
              <div><Label className="text-[#E2E8F0] text-xs">Prix (€)</Label><Input type="number" value={form.price || ''} onChange={e => set('price', e.target.value)} placeholder="99" className="mt-1 bg-white/5 border-white/10 text-white text-sm" /></div>
            </div>
            <div>
              <Label className="text-[#E2E8F0] text-xs">Attribuer a un vendeur (optionnel)</Label>
              <Select value={form.vendor_id || 'none'} onValueChange={v => set('vendor_id', v === 'none' ? '' : v)}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white text-sm mt-1"><SelectValue placeholder="Aucun vendeur" /></SelectTrigger>
                <SelectContent className="bg-[#1A1A2E] border-white/10">
                  <SelectItem value="none" className="text-white">Aucun vendeur</SelectItem>
                  {vendors.map(v => <SelectItem key={v.id} value={v.id} className="text-white">{v.business_name || v.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={createMerchant} disabled={creating} className="btn-primary w-full" data-testid="admin-merchant-submit">{creating ? 'Creation...' : 'Creer le marchand'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* MERCHANT DETAIL DIALOG */}
      <Dialog open={!!detailData} onOpenChange={() => setDetailData(null)}>
        <DialogContent className="bg-[#1A1A2E] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          {detailData && (
            <>
              <DialogHeader>
                <DialogTitle className="text-white" style={{ fontFamily: 'Outfit' }}>{detailData.merchant?.business_name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg bg-white/5"><p className="text-xs text-[#94A3B8]">Email</p><p className="text-sm text-white font-mono">{detailData.merchant?.email}</p></div>
                  <div className="p-3 rounded-lg bg-white/5"><p className="text-xs text-[#94A3B8]">Mot de passe</p><p className="text-sm text-[#F59E0B] font-mono">{detailData.merchant?.vendor_password || '—'}</p></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 rounded-lg bg-white/5"><p className="text-xs text-[#94A3B8]">Total spins</p><p className="text-xl font-bold text-[#7C3AED]">{detailData.stats?.total_spins || 0}</p></div>
                  <div className="p-3 rounded-lg bg-white/5"><p className="text-xs text-[#94A3B8]">Lots gagnes</p><p className="text-xl font-bold text-[#F59E0B]">{detailData.stats?.total_prizes || 0}</p></div>
                  <div className="p-3 rounded-lg bg-white/5"><p className="text-xs text-[#94A3B8]">Credits</p><p className="text-xl font-bold text-[#10B981]">{detailData.credits || 0}€</p></div>
                </div>
                {detailData.config?.slots && (
                  <div>
                    <p className="text-sm font-medium text-white mb-2">Configuration roue</p>
                    <div className="space-y-1">
                      {detailData.config.slots.map((s, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm">
                          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: s.color }} />
                          <span className="text-[#E2E8F0] flex-1">{s.label}</span>
                          <span className="text-[#94A3B8]">{s.probability}%</span>
                          {s.is_losing && <Badge className="bg-[#EF4444]/20 text-[#EF4444] text-[10px]">Perdu</Badge>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {detailData.recent_spins?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-white mb-2">Derniers spins</p>
                    <div className="space-y-1 max-h-48 overflow-y-auto">
                      {detailData.recent_spins.map((s, i) => (
                        <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-white/5">
                          <span className="text-[#94A3B8]">{s.created_at?.slice(0, 16).replace('T', ' ')}</span>
                          <span className="text-white">{s.first_name} {s.last_name}</span>
                          <Badge className={s.is_losing ? 'bg-[#EF4444]/20 text-[#EF4444] text-[10px]' : 'bg-[#10B981]/20 text-[#10B981] text-[10px]'}>{s.prize_won}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* CREDIT ADJUSTMENT DIALOG */}
      <Dialog open={!!creditDialog} onOpenChange={() => setCreditDialog(null)}>
        <DialogContent className="bg-[#1A1A2E] border-white/10 text-white max-w-sm">
          <DialogHeader><DialogTitle className="text-white" style={{ fontFamily: 'Outfit' }}>Ajuster les credits</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[#E2E8F0] text-xs">Montant (€) — positif pour ajouter, negatif pour retirer</Label>
              <Input type="number" value={creditAmount} onChange={e => setCreditAmount(e.target.value)} placeholder="5.00" step="0.01"
                className="mt-1 bg-white/5 border-white/10 text-white text-sm" data-testid="admin-credit-amount" />
            </div>
            <div>
              <Label className="text-[#E2E8F0] text-xs">Raison (optionnel)</Label>
              <Input value={creditReason} onChange={e => setCreditReason(e.target.value)} placeholder="Ajustement admin"
                className="mt-1 bg-white/5 border-white/10 text-white text-sm" />
            </div>
            <Button onClick={adjustCredits} className="btn-primary w-full" data-testid="admin-credit-submit">Appliquer</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OverviewTab({ stats }) {
  if (!stats) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-3 border-[#7C3AED] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const statCards = [
    { label: 'Vendeurs', value: stats.total_vendors, icon: Users, color: '#7C3AED' },
    { label: 'Marchands', value: stats.total_merchants, icon: Store, color: '#3B82F6', sub: `${stats.active_merchants} actifs` },
    { label: 'Total spins', value: stats.total_spins, icon: Zap, color: '#F59E0B', sub: `${stats.recent_spins} cette semaine` },
    { label: 'Revenus', value: `${stats.total_revenue}€`, icon: DollarSign, color: '#10B981' },
    { label: 'Campagnes', value: stats.total_campaigns, icon: Send, color: '#EC4899' },
    { label: 'Credits en circulation', value: `${stats.total_credits}€`, icon: CreditCard, color: '#14B8A6' },
  ];

  const maxSpins = Math.max(...stats.spins_by_day.map(d => d.count), 1);

  return (
    <div className="space-y-6" data-testid="admin-overview">
      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {statCards.map((s, i) => (
          <div key={i} className="rounded-xl p-4 flex items-center gap-4" style={{ background: '#1A1A2E', border: '1px solid rgba(255,255,255,0.05)' }}>
            <div className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ background: `${s.color}15` }}>
              <s.icon className="w-5 h-5" style={{ color: s.color }} />
            </div>
            <div>
              <p className="text-xs text-[#94A3B8]">{s.label}</p>
              <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>{s.value}</p>
              {s.sub && <p className="text-[10px] text-[#94A3B8]">{s.sub}</p>}
            </div>
          </div>
        ))}
      </div>

      {/* Spins Chart */}
      <div className="rounded-xl p-5" style={{ background: '#1A1A2E', border: '1px solid rgba(255,255,255,0.05)' }}>
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-4 h-4 text-[#7C3AED]" />
          <h3 className="text-sm font-semibold text-white" style={{ fontFamily: 'Outfit' }}>Spins des 7 derniers jours</h3>
        </div>
        <div className="flex items-end gap-2 h-32">
          {stats.spins_by_day.map((d, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-1">
              <span className="text-[10px] text-[#94A3B8]">{d.count}</span>
              <div className="w-full rounded-t-md transition-all" style={{
                height: `${Math.max((d.count / maxSpins) * 100, 4)}%`,
                background: `linear-gradient(to top, #7C3AED, #A855F7)`,
                minHeight: '4px'
              }} />
              <span className="text-[10px] text-[#94A3B8]">{d.day}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
